import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

/** A surface that groups related content. */
export function Card({ className, ...props }: CardProps) {
  return (
    <div
      className={cn("rounded-[var(--radius-card)] border border-rule/80 bg-surface", className)}
      {...props}
    />
  );
}

/** A card's heading region. */
export function CardHeader({ className, ...props }: CardProps) {
  return (
    <div
      className={cn("flex flex-col gap-1 border-b border-rule px-5 py-4", className)}
      {...props}
    />
  );
}

/** A card's title. */
export function CardTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn("font-sans text-prose font-semibold text-ink", className)} {...props}>
      {children}
    </h3>
  );
}

/** Supporting text under a card title. */
export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-prose text-ink-2", className)} {...props} />;
}

/** A card's body. */
export function CardBody({ className, ...props }: CardProps) {
  return <div className={cn("px-5 py-4", className)} {...props} />;
}

/** A card's action row. */
export function CardFooter({ className, ...props }: CardProps) {
  return (
    <div
      className={cn("flex items-center gap-2 border-t border-rule px-5 py-3", className)}
      {...props}
    />
  );
}
